import { Api, JettonsBalances, TonApiClient, TraceIDs } from "@ton-api/client";
import { Address } from "@ton/core";

interface ICachedData<T> {
    data: T;
    updatedAt: number;
}

export class TonApi {
    private api: Api<TonApiClient>;
    private readonly CACHE_KEY = "jettons_cache";
    private readonly CACHE_EXPIRY = 1000 * 60 * 5; // 5 minutes in milliseconds
    private readonly BATCH_SIZE = 100; // Number of jettons to fetch per API call
    private readonly RATE_LIMIT_DELAY = 500; // Delay between batch requests in milliseconds

    constructor() {
        const http = new TonApiClient({
            baseUrl: "https://tonapi.io",
            // Add API key if needed - you may need to obtain one from tonapi.io
            // headers: { 'Authorization': 'Bearer YOUR_API_KEY' }
        });

        this.api = new Api(http);
    }

    public async getTraces(address: string): Promise<TraceIDs> {
        return this.api.accounts.getAccountTraces(Address.parse(address), { limit: 100 });
    }

    public async getAddressJettons(address: string): Promise<JettonsBalances> {
        const chachedData = this.getCachedData(this.CACHE_KEY + address + "getWalletBalance");

        if (chachedData && chachedData.updatedAt > Date.now() - this.CACHE_EXPIRY) {
            try {
                for (let i = 0; i < chachedData.data.balances.length; i++) {
                    const j = chachedData.data.balances[i];
                    let address = this.getJettonAddress(j?.jetton);
                    if (address) {
                        chachedData.data.balances[i].jetton.address = address;
                    }
                }

                return chachedData.data;
            } catch (error) {
                this.clearCache(this.CACHE_KEY + address + "getWalletBalance");
            }
        }

        const response = await this.api.accounts.getAccountJettonsBalances(Address.parse(address), {
            currencies: ["ton,usd,rub"],
        });

        this.setCache(
            { data: response, updatedAt: Date.now() } as ICachedData<any>,
            this.CACHE_KEY + address + "getWalletBalance",
        );

        return response;
    }

    public async getTonBalance(address: string) {
        return this.api.accounts.getAccount(Address.parse(address));
    }

    public async getTonRates() {
        const response = await this.api.rates.getRates({ tokens: ["ton"], currencies: ["usd,rub"] });
        return response.rates;
    }

    public getJettonAddress(jetton: any): string | null {
        try {
            if (jetton.address?.hash?.data) {
                const workChain = jetton.address.workChain ?? 0;
                const hashBuffer = Buffer.from(jetton.address.hash.data);
                return new Address(workChain, hashBuffer).toString();
            }

            if (jetton.address) {
                return Address.parse(jetton.address).toString();
            }

            return null;
        } catch (e) {
            console.error("Error parsing jetton address:", e);
            return null;
        }
    }

    private getCachedData(cacheKey: string): ICachedData<any> | null {
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
            try {
                const data = JSON.parse(cached);
                if (data.updatedAt && data.data) {
                    return data;
                }
            } catch (error) {
                console.error("Invalid cache data", error);
                return null;
            }
        }
        return null;
    }
    private setCache(data: ICachedData<any>, cacheKey: string): void {
        localStorage.setItem(cacheKey, JSON.stringify(data));
    }
    private clearCache(cacheKey: string): void {
        localStorage.removeItem(cacheKey);
    }
}
